import { Tooltip } from "@mui/material";
import cn from "../../utils/cn";

// Display helpers shared by the AI interview views

// The API sometimes sends objects where the docs say strings (e.g. candidate_questions items are
// { question, answered_by_ai }), so anything rendered as text goes through this.
const TEXT_KEYS = ["text", "question", "point", "statement", "message", "description", "name", "claim", "value"];
export const toText = (value) => {
  if (value === null || value === undefined || typeof value === "boolean") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(toText).filter(Boolean).join(", ");
  if (typeof value === "object") {
    const key = TEXT_KEYS.find((name) => typeof value[name] === "string" || typeof value[name] === "number");
    if (key) return String(value[key]);
    return Object.values(value).map(toText).filter(Boolean).join(" · ");
  }
  return String(value);
};

export const humanize = (value) => (value ? String(value).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "");
export const isScore = (value) => typeof value === "number" && !Number.isNaN(value);

export const formatDateTime = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

export const formatTime = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

export const formatDuration = (seconds) => {
  if (!isScore(seconds)) return "";
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return minutes ? `${minutes}m ${rest}s` : `${rest}s`;
};

export const SECTION_LABELS = {
  intro: "Intro",
  resume: "Resume",
  technical: "Technical",
  problem_solving: "Problem solving",
  behavioral: "Behavioral",
};

export const PRIORITY_ORDER = { must_have: 0, important: 1, nice_to_have: 2 };
export const PRIORITY_LABELS = { must_have: "Must have", important: "Important", nice_to_have: "Nice to have" };
export const byPriority = (getPriority) => (a, b) =>
  (PRIORITY_ORDER[getPriority(a)] ?? 3) - (PRIORITY_ORDER[getPriority(b)] ?? 3);

export const Section = ({ title, children, className }) => (
  <section className={cn("flex flex-col gap-2", className)}>
    <h3 className="text-sm font-semibold">{title}</h3>
    {children}
  </section>
);

export const Chips = ({ items, className }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((item, index) => (
      <span key={index} className={cn("rounded-full border px-2.5 py-0.5 text-xs", className || "bg-gray-50 border-gray-300")}>
        {toText(item)}
      </span>
    ))}
  </div>
);

export const BulletList = ({ items, format = toText }) => (
  <ul className="list-disc pl-5 text-sm flex flex-col gap-1">
    {(Array.isArray(items) ? items : [items]).map((item, index) => <li key={index}>{format(item)}</li>)}
  </ul>
);

export const ScoreBar = ({ label, value, max = 10 }) => (
  <div className="flex flex-col gap-1">
    <div className="flex justify-between text-xs">
      <span>{label}</span>
      <span className="font-medium">{isScore(value) ? `${value.toFixed(1)}/${max}` : "—"}</span>
    </div>
    <div className="h-2 rounded-full bg-gray-200 overflow-hidden" aria-hidden="true">
      {isScore(value) && (
        <div className="h-full rounded-full bg-blue-600" style={{ width: `${(Math.max(0, Math.min(max, value)) / max) * 100}%` }} />
      )}
    </div>
  </div>
);

const SEVERITY_CLASSES = {
  critical: "border-red-300 bg-red-100 text-red-800",
  warning: "border-amber-300 bg-amber-100 text-amber-800",
  info: "border-gray-300 bg-gray-100 text-gray-700",
};

// Note-taker housekeeping that isn't useful at a glance; still listed under the report's technical issues
const HIDDEN_CHIP_CODES = ["BOT_MIC_ON", "CAPTIONS_UNAVAILABLE"];
export const chipWarnings = (warnings) => (warnings || []).filter((warning) => !HIDDEN_CHIP_CODES.includes(warning.code));

// Warning[] from the AI session or report as small chips: critical red, warning amber, info grey; message on hover
export const WarningChips = ({ warnings, className }) => {
  const shown = chipWarnings(warnings);
  if (!shown.length) return null;
  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {shown.map((warning, index) => (
        <Tooltip key={`${warning.code}-${index}`} title={toText(warning.message)} arrow>
          <span
            tabIndex={0}
            className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap",
              SEVERITY_CLASSES[warning.severity] || SEVERITY_CLASSES.info
            )}
            aria-label={`${humanize((warning.code || "").toLowerCase())}: ${toText(warning.message)}`}
          >
            {humanize((warning.code || "").toLowerCase())}
            {warning.count > 1 ? ` ×${warning.count}` : ""}
          </span>
        </Tooltip>
      ))}
    </div>
  );
};

// Warning[] as a readable list (report integrity and technical sections)
export const WarningList = ({ warnings }) => (
  <ul className="flex flex-col gap-1.5 text-sm">
    {warnings.map((warning, index) => (
      <li key={`${warning.code}-${index}`} className="flex flex-wrap items-baseline gap-2">
        <span
          className={cn(
            "rounded-full border px-2 py-0.5 text-[11px] font-medium",
            SEVERITY_CLASSES[warning.severity] || SEVERITY_CLASSES.info
          )}
        >
          {humanize(warning.severity)}
        </span>
        <span>
          {toText(warning.message)}
          {warning.count > 1 ? ` (${warning.count} times)` : ""}
        </span>
      </li>
    ))}
  </ul>
);

export const LoadingLine = ({ children }) => (
  <div className="flex items-center gap-2 text-sm" role="status">
    <span className="inline-block h-4 w-4 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" aria-hidden="true" />
    {children}
  </div>
);

export const ErrorBox = ({ children, onRetry }) => (
  <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm flex flex-wrap items-center justify-between gap-2" role="alert">
    <span>{children}</span>
    {onRetry && (
      <button type="button" onClick={onRetry} className="underline font-medium">
        Try again
      </button>
    )}
  </div>
);
